import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { formatDateShort } from "../AdminPatients/clientSportDisplay";
import { isMembershipFrozenNow } from "../../../utils/membershipAssignUtils";
import s from "../clinic.module.css";
import { Button, Input, useToast } from "../../ui";

function Stepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  disabled = false,
  ariaLabel = "Количество",
}) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : min;

  const set = (next) => {
    const clamped = Math.max(min, Math.min(max, next));
    onChange(String(clamped));
  };

  return (
    <div className={s.membershipStepper}>
      <button
        type="button"
        className={s.membershipStepperBtn}
        aria-label="Минус"
        disabled={disabled || safe <= min}
        onClick={() => set(safe - 1)}
      >
        −
      </button>
      <input
        className={s.membershipStepperInput}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step="1"
        value={value}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange("");
            return;
          }
          const v = Number(raw);
          if (!Number.isFinite(v)) return;
          onChange(String(Math.max(min, Math.min(max, Math.trunc(v)))));
        }}
      />
      <button
        type="button"
        className={s.membershipStepperBtn}
        aria-label="Плюс"
        disabled={disabled || safe >= max}
        onClick={() => set(safe + 1)}
      >
        +
      </button>
    </div>
  );
}

export default function MembershipAdjustPanel({
  membership,
  onChanged,
  initialOpen = false,
  onRequestClose,
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(initialOpen);
  const [visitsLeft, setVisitsLeft] = useState(
    String(membership?.visits_remaining ?? 0),
  );
  const [termDays, setTermDays] = useState("7");
  const [endsAt, setEndsAt] = useState(membership?.ends_at || "");

  useEffect(() => {
    setVisitsLeft(String(membership?.visits_remaining ?? 0));
    setEndsAt(membership?.ends_at || "");
    if (initialOpen) setOpen(true);
  }, [membership?.id, membership?.visits_remaining, membership?.ends_at, initialOpen]);

  if (!membership) return null;

  const frozen = isMembershipFrozenNow(membership);
  const isVisits =
    membership.plan?.plan_type === "visits" ||
    membership.visits_remaining != null;
  const hasEndDate = Boolean(membership.ends_at);
  const currentVisits = membership.visits_remaining ?? 0;
  const visitsDirty =
    visitsLeft !== "" && Number(visitsLeft) !== currentVisits;
  const dateDirty = Boolean(endsAt) && endsAt !== (membership.ends_at || "");

  const patch = async (payload, successMessage = "Сохранено") => {
    setBusy(true);
    try {
      await clinicAPI.updateClientMembership(membership.id, payload);
      toast.success(successMessage);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className={s.membershipAdjustToggle}
        onClick={() => setOpen(true)}
      >
        Изменить занятия или срок
      </button>
    );
  }

  const confirmBurn = (label) => {
    if (!hasEndDate && isVisits && currentVisits > 0) {
      return window.confirm(
        `Сейчас ${currentVisits} зан. без срока. ${label} Неиспользованные занятия после этой даты сгорят. Продолжить?`,
      );
    }
    return true;
  };

  const saveVisits = async () => {
    const n = Number(visitsLeft);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      toast.error("Укажите целое число от 0");
      return;
    }
    if (n === currentVisits) {
      toast.error("Количество не изменилось");
      return;
    }
    await patch(
      { visits_remaining: n },
      n > currentVisits
        ? `Добавлено ${n - currentVisits} зан.`
        : `Списано ${currentVisits - n} зан.`,
    );
  };

  const extendTerm = async () => {
    const days = Number(termDays);
    if (!Number.isFinite(days) || !Number.isInteger(days) || days < 1) {
      toast.error("Укажите число дней от 1");
      return;
    }
    if (!confirmBurn(`Срок станет ещё ${days} дн. с сегодня.`)) return;
    await patch(
      { extend_days: days },
      hasEndDate
        ? `Срок продлён на ${days} дн.`
        : `Назначен срок: ещё ${days} дн.`,
    );
    setTermDays("7");
  };

  const saveEndDate = async () => {
    if (!endsAt) {
      toast.error("Укажите дату окончания");
      return;
    }
    if (!dateDirty) {
      toast.error("Дата не изменилась");
      return;
    }
    if (!confirmBurn(`Окончание: ${formatDateShort(endsAt)}.`)) return;
    await patch({ ends_at: endsAt }, `Срок до ${formatDateShort(endsAt)}`);
  };

  return (
    <div
      className={`${s.membershipAdjustForm} ${
        initialOpen ? s.membershipAdjustFormSheet : ""
      }`}
    >
      {isVisits ? (
        <section className={s.membershipAdjustBlock}>
          <header className={s.membershipAdjustHead}>
            <h4 className={s.membershipAdjustBlockTitle}>Занятия</h4>
            <span className={s.membershipAdjustNow}>сейчас {currentVisits}</span>
          </header>
          <div className={s.membershipAdjustControls}>
            <Stepper
              value={visitsLeft}
              onChange={setVisitsLeft}
              min={0}
              disabled={busy || frozen}
              ariaLabel="Остаток занятий"
            />
            <Button
              type="button"
              size="sm"
              className={s.membershipAdjustActionBtn}
              disabled={busy || frozen || !visitsDirty}
              onClick={saveVisits}
            >
              Сохранить
            </Button>
          </div>
        </section>
      ) : null}

      <section className={s.membershipAdjustBlock}>
        <header className={s.membershipAdjustHead}>
          <h4 className={s.membershipAdjustBlockTitle}>
            {hasEndDate ? "Срок" : "Срок (сейчас без ограничения)"}
          </h4>
          <span className={s.membershipAdjustNow}>
            {hasEndDate
              ? `до ${formatDateShort(membership.ends_at)}`
              : "без даты"}
          </span>
        </header>

        <div className={s.membershipAdjustControls}>
          <Stepper
            value={termDays}
            onChange={setTermDays}
            min={1}
            disabled={busy || frozen}
            ariaLabel="Дней"
          />
          <Button
            type="button"
            size="sm"
            className={s.membershipAdjustActionBtn}
            disabled={busy || frozen}
            onClick={extendTerm}
          >
            {hasEndDate ? "Продлить" : "Назначить"}
          </Button>
        </div>

        <div className={s.membershipAdjustPresets} role="group" aria-label="Быстрый выбор">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              type="button"
              className={
                String(d) === String(termDays)
                  ? s.membershipAdjustPresetOn
                  : s.membershipAdjustPreset
              }
              disabled={busy || frozen}
              onClick={() => setTermDays(String(d))}
            >
              {d} дн.
            </button>
          ))}
        </div>

        <div className={s.membershipAdjustDateBlock}>
          <label className={s.membershipAdjustDateLabel} htmlFor={`mem-end-${membership.id}`}>
            Точная дата окончания
          </label>
          <div className={`${s.membershipAdjustControls} ${s.membershipAdjustDateControls}`}>
            <Input
              id={`mem-end-${membership.id}`}
              type="date"
              className={s.membershipAdjustDateInput}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              disabled={busy || frozen}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={s.membershipAdjustActionBtn}
              disabled={busy || frozen || !dateDirty}
              onClick={saveEndDate}
            >
              Поставить
            </Button>
          </div>
        </div>
      </section>

      <div className={s.membershipAdjustFooter}>
        <button
          type="button"
          className={s.membershipAdjustToggle}
          disabled={busy}
          onClick={() => {
            if (onRequestClose) onRequestClose();
            else setOpen(false);
          }}
        >
          {onRequestClose ? "Закрыть" : "Скрыть"}
        </button>
        {membership.status === "expired" ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => patch({ status: "active" }, "Абонемент восстановлен")}
          >
            Восстановить
          </Button>
        ) : null}
      </div>

      {frozen ? (
        <p className={s.clientTabMuted}>
          Пока абонемент заморожен, корректировка недоступна.
        </p>
      ) : null}
    </div>
  );
}
